import { create, } from "zustand";
import { DesignCategory, DesignType, InsoleDesign } from "../models/InsoleDesign";
import { Insole, PressureImage } from "../models/Insole";
import { Addition } from "models/Addition";
import { Group, Mesh } from 'three';
import { Engraving } from "models/Engraving";
import PressureData from "Scan/PressureData";
import { Patient } from "models/Patient";
import { PressureAlignment } from "models/DesignPressureAlignment";
import { Hardness } from "components/Viewer3d/consts";
import { GCodeSetting } from "models/GcodeSetting";

interface DesignStore {
  insoleDesign: InsoleDesign | null;
  previousDesign: InsoleDesign | null;
  temporaryDesign: InsoleDesign | null;
  previousEngraving: Engraving | null;
  setDesign: (design: InsoleDesign) => void;
  designLoaded: boolean;
  undoLoaded: boolean;
  mirrorLoaded: boolean;
  setDesignLoaded: (loaded: boolean) => void;
  newAddition: Addition | null;
  pushAdditionToDesign: (newAddition: Addition, side: number) => void;

  setUpdatedMesh: (mesh: Mesh) => void;

  setGCode: (gcodes: Mesh[]) => void;
  getGCode: () => Mesh[] | null;
  gCodes: Mesh[];
  GcodeReady: boolean;

  setDesignName: (name: string) => void;
  additionRemoved: { 'side': number, 'index': number, 'isAdditionID': boolean }[];
  removeAdditionFromDesign: (side: number, index: number) => void;
  additionHeightUpdated: { 'side': number, 'index': number, 'value': number };
  /**
   * Request to slide an addition across the insole, in millimetres.
   *
   * dx is medial/lateral and dz is heel/toe, matching the axes the drag gizmo
   * works in. Viewer3D applies it with the same primitive the gizmo uses.
   */
  additionMoved: { 'side': number, 'index': number, 'dx': number, 'dz': number };
  setAdditionHeightUpdated: (data: { side: number; index: number; value: number }) => void;
  additionHardnessUpdated: { 'side': number, 'index': number, 'value': Hardness };
  setAdditionHardnessUpdated: (data: {
    side: number;
    index: number;
    value: Hardness;
  }) => void;

  importPressureImageData: (newRightMat: PressureImage | undefined, newLeftMat: PressureImage | undefined) => void;
  pressureImageData: (PressureImage | undefined)[];
  RondomImageData: (PressureImage | undefined)[];

  setPressure: (pData: PressureData) => void;
  pressureAlignment: PressureAlignment | null;
  setPressureAlignment: (pressureAlignment: PressureAlignment | null) => void;

  engraving: Engraving;
  setEngraving: (newEngraving: Engraving) => void;
  setCategory: (category: DesignCategory) => void;
  categoryChanged: boolean;
  thirdQuarter: boolean;

  patient: Patient | null;
  patientConditionUpdated: boolean;

  gcodeSetting: GCodeSetting[];
}

export const useDesignStore = create<DesignStore>((set, get) => ({
  insoleDesign: null,
  previousDesign: null,
  previousEngraving: null,
  temporaryDesign: null,
  designLoaded: false,
  undoLoaded: false,
  mirrorLoaded: false,
  categoryChanged: false,
  newAddition: null,
  additionRemoved: [],
  additionHeightUpdated: { 'side': -1, 'index': -1, 'value': -1 },
  additionMoved: { 'side': -1, 'index': -1, 'dx': 0, 'dz': 0 },
  additionHardnessUpdated: { 'side': -1, 'index': -1, 'value': Hardness.Medium },
  gCodes: [],
  GcodeReady: false,
  engraving: new Engraving(),
  patient: null,
  patientConditionUpdated: false,
  pressureAlignment: null,
  pressureImageData: [undefined, undefined],
  RondomImageData: [undefined, undefined],
  gcodeSetting: [],
  thirdQuarter: false,
  setDesign: (design: InsoleDesign) => set({ insoleDesign: design }),
  setDesignLoaded: (loaded: boolean) => set({ designLoaded: loaded }),
  setEngraving: (newEngraving: Engraving) => set({ engraving: newEngraving }),
  setAdditionHeightUpdated: (data) => { set({ additionHeightUpdated: data }); },
  setAdditionHardnessUpdated: (data) => { set({ additionHardnessUpdated: data }); },
  setCategory: (category: DesignCategory) => {
    const design = get().insoleDesign;
    if (!design) return;

    if (category == DesignCategory.Sandal) design.isSandal = true;
    else design.isSandal = false;

    design.category = category;

    set({ categoryChanged: true });
    set({ insoleDesign: design })
  },

  pushAdditionToDesign: (newAddition, side) => {
    const design = get().insoleDesign;
    if (!design) return;
    if (newAddition === null) return;

    const footIndex = side;

    const updatedDesign = { ...design };
    const updatedInsole = new Insole();
    Object.assign(updatedInsole, updatedDesign.insoles[footIndex]);

    // Initialize additions array if it doesn't exist
    if (!updatedInsole.additions) {
      updatedInsole.additions = [];
    }


    updatedInsole.additions = [...updatedInsole.additions, newAddition];
    updatedDesign.insoles[footIndex] = updatedInsole;
    //get().setUpdatedAddition(null);

    set({ insoleDesign: updatedDesign });
    set({ newAddition: newAddition });
  },
  setUpdatedMesh: (mesh: Mesh) => {
    const design = get().insoleDesign;
    if (!design) return;

    const footIndex = mesh.userData.foot;

    const updatedDesign = { ...design };
    const updatedInsole = Object.assign({}, updatedDesign.insoles[footIndex]);

    updatedInsole.mainMesh = mesh;
    updatedDesign.insoles[footIndex] = updatedInsole;

    set({ insoleDesign: updatedDesign });
  },
  setPressure: (pressureData: any) => {
    const design = get().insoleDesign;
    if (!design) return;

    const updatedDesign = { ...design };
    updatedDesign.pressureData = pressureData;

    set({ insoleDesign: updatedDesign })
  },
  setPressureAlignment: (pressureAlignment: PressureAlignment | null) => {
    set({ pressureAlignment: pressureAlignment })
  },
  setGCode: (gcodes: Mesh[]) => {
    set({ gCodes: gcodes });

    if (gcodes.length > 0) {
      set({ GcodeReady: true });
    }
    else {
      set({ GcodeReady: false });
    }
  },
  getGCode: () => {
    const design = get().insoleDesign;
    if (!design) return null;

    return get().gCodes;
  },
  setDesignName: (name: string) => {
    const design = get().insoleDesign;
    if (!design) return;

    const updatedDesign = { ...design, name };
    set({ insoleDesign: updatedDesign });

    const engraving = get().engraving;
    if (!engraving) return;

    engraving.updateText(name);
  },
  removeAdditionFromDesign: (side, designAdditionId) => {
    const design = get().insoleDesign;
    if (!design) return;

    const updatedDesign = { ...design };
    const updatedInsole = Object.assign({}, updatedDesign.insoles[side]);
    let additionIndex = updatedInsole.additions.findIndex(add => add.designAdditionId === designAdditionId);
    // Remove the addition at the specified index
    updatedInsole.additions = [
      ...updatedInsole.additions.slice(0, additionIndex),
      ...updatedInsole.additions.slice(additionIndex + 1),
    ];

    updatedDesign.insoles[side] = updatedInsole;

    set({ insoleDesign: updatedDesign });
  },
  importPressureImageData: (newRightMat, newLeftMat) => {
    const design = get().insoleDesign;
    if (!design) return;

    set({ pressureImageData: [newRightMat, newLeftMat] });

    const updateDesign = { ...design };
    const rightInsole = new Insole();
    const leftInsole = new Insole();

    Object.assign(rightInsole, updateDesign.insoles[0]);
    rightInsole.pressureImageData = (newRightMat) ? newRightMat.image : undefined;

    Object.assign(leftInsole, updateDesign.insoles[1]);
    leftInsole.pressureImageData = (newLeftMat) ? newLeftMat.image : undefined;

    set({ insoleDesign: updateDesign });
  }
  // setInsoleBones: (bones: Group, insoleIndex: number) => {
  //   set((state) => {
  //     if (!state.insoleDesign) return state;

  //     const insoles = [...state.insoleDesign.insoles];

  //     insoles[insoleIndex] = {
  //       ...insoles[insoleIndex],
  //       bonesGroup
  //     };

  //     return {
  //       insoleDesign: {
  //         ...state.insoleDesign,
  //         insoles
  //       }
  //     };
  //   });
  // }



}));

